// Sistem Performansı paneli: aralık, kopya filtresi, sekmeler ve izleme aç/kapat
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Activity, RefreshCw, Power } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { ownerApi, apiError } from '../ownerApi';
import { ReauthDialog } from '../ReauthDialog';
import { PerfOverview } from './PerfOverview';
import { PerfResources } from './PerfResources';
import { ApiTable } from './ApiTable';
import { PerfActions } from './PerfActions';
import { PerfDiagnosis } from './PerfDiagnosis';
import { PerfAlerts } from './PerfAlerts';
import { PerfReports } from './PerfReports';
import { RANGES } from './perfFormat';

const TABS = [['overview', 'Genel Bakış'], ['diagnosis', 'Otomatik Teşhis'], ['alerts', 'Uyarılar'], ['actions', 'Kullanıcı İşlemleri'], ['apis', 'API Performansı'], ['resources', 'Sunucu Kaynakları'], ['reports', 'Teknik Raporlar']];

export const PerfPanel = () => {
  const [range, setRange] = useState('24h');
  const [tab, setTab] = useState('overview');
  const [pod, setPod] = useState('all');
  const [data, setData] = useState({});
  const [pods, setPods] = useState([]);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState('');
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = `range=${range}&pod=${pod}`;
      const [o, a, r, s, dg] = await Promise.all([ownerApi.get(`/platform-owner/perf/overview?${q}`), ownerApi.get(`/platform-owner/perf/apis?${q}`),
        ownerApi.get(`/platform-owner/perf/resources?range=${range}`), ownerApi.get('/platform-owner/perf/settings'), ownerApi.get('/platform-owner/perf/diagnosis')]);
      setData({ overview: o.data, apis: a.data, resources: r.data, diagnosis: dg.data }); setPods(r.data.pods.map(p => p.pod)); setSettings(s.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [range, pod]);
  useEffect(() => { load(); }, [load]);

  const toggle = (pw) => ownerApi.put('/platform-owner/perf/settings', { password: pw, enabled: !settings.enabled })
    .then(r => { setSettings(s => ({ ...s, enabled: r.data.enabled })); setAction(null); toast.success(r.data.enabled ? 'İzleme açıldı' : 'İzleme kapatıldı'); })
    .catch(e => toast.error(apiError(e)));
  const apis = (data.apis || []).filter(a => a.api.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="perf-panel">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-500"><Activity className="h-5 w-5" /></span>
        <div className="mr-auto"><h2 className="text-xl font-semibold text-slate-900">Sistem Performansı</h2>
          <p className="text-xs text-slate-500">Sunucu içi süreler · yüzdelikler dağılım sınıflarından hesaplanır (yaklaşık ±%15) · veriler 5 dakikada bir yazılır</p></div>
        {settings && <span className={`rounded-full px-3 py-1 text-xs font-semibold ${settings.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`} data-testid="perf-enabled-state">{settings.enabled ? 'İzleme açık' : 'İzleme kapalı'}</span>}
        {settings && <Button variant="outline" size="sm" onClick={() => setAction({ title: settings.enabled ? 'İzlemeyi kapat' : 'İzlemeyi aç', description: 'Bu sunucu kopyasında hemen, diğer kopyalarda en geç 5 dakika içinde uygulanır.', confirmLabel: settings.enabled ? 'Kapat' : 'Aç', danger: settings.enabled, go: toggle })} data-testid="perf-toggle"><Power className="mr-2 h-4 w-4" />{settings.enabled ? 'Kapat' : 'Aç'}</Button>}
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="perf-refresh"><RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Yenile</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map(([k, l]) => <button key={k} type="button" onClick={() => setRange(k)} className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${range === k ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'}`} data-testid={`perf-range-${k}`}>{l}</button>)}
        <select value={pod} onChange={e => setPod(e.target.value)} className="ml-auto rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm" data-testid="perf-pod-select">
          <option value="all">Tüm kopyalar (birleşik)</option>{pods.map(p => <option key={p} value={p}>Kopya {p}</option>)}
        </select>
      </div>
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${tab === k ? 'border-orange-500 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-testid={`perf-tab-${k}`}>{l}{k === 'alerts' && data.diagnosis?.open_alerts > 0 && <span className="ml-1.5 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="perf-alerts-badge">{data.diagnosis.open_alerts}</span>}</button>)}
      </div>
      {tab === 'overview' && data.overview && <PerfOverview data={data.overview} range={range} />}
      {tab === 'apis' && data.apis && (
        <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="perf-apis">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><p className="text-sm text-slate-600">{apis.length} API · istek sayısına göre</p>
            <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="API ara (ör. /employees)" className="max-w-xs" data-testid="perf-api-filter" /></div>
          <ApiTable rows={apis} showAll testId="perf-api-table" />
        </section>
      )}
      {tab === 'diagnosis' && data.diagnosis && <PerfDiagnosis data={data.diagnosis} onRefresh={load} />}
      {tab === 'alerts' && <PerfAlerts alertEmail={settings?.alert_email} onChanged={load} />}
      {tab === 'reports' && <PerfReports />}
      {tab === 'actions' && <PerfActions range={range} />}
      {tab === 'resources' && data.resources && <PerfResources data={data.resources} range={range} />}
      <ReauthDialog action={action} onClose={() => setAction(null)} onConfirm={(pw) => action.go(pw)} />
    </div>
  );
};
